/**
 * Gestiona la paleta personal del panel y la guarda en builders.palette para recuperarla
 * en otros navegadores. El tamaño de letra se gestiona aparte.
 *
 * data-palette se aplica en html junto a data-theme para calcular los colores
 * correctamente. Una copia local permite pintar el color elegido antes de cargar la
 * cuenta. Los elementos que declaran su propia paleta conservan su apariencia.
 */
import { isPanelPath } from "@shared/appPaths";
import { CUSTOM_PALETTE } from "@shared/palettes";
import type { AppTheme } from "@shared/types";

import { appBrand } from "./appTheme";
import { pb } from "./pb";
import { location } from "./router.svelte";
import { session } from "./session.svelte";

const KEY = "plane-panel-palette";

/** La paleta del panel, sin el tamaño de letra: ese mando es otro. */
function clean(theme?: AppTheme | null): AppTheme {
  const { palette, color } = appBrand(theme);
  return color ? { palette, color } : { palette };
}

/** Paleta del panel guardada en este navegador, para pintar antes de que llegue la del servidor. */
function cached(): AppTheme {
  try {
    return clean(JSON.parse(localStorage.getItem(KEY) ?? "null"));
  } catch {
    return { palette: null };
  }
}

let fallback = cached();
/** Lo que se está probando en los ajustes antes de guardar. */
let preview = $state<AppTheme | null>(null);

/* Con sesión manda la cuenta; sin ella (la pantalla de entrar) la última que
   se vio en este navegador. */
const saved = () => (session.me ? clean(session.me.palette) : fallback);

/**
 * Fuera de `/admin` no se pone ninguna: ni una aplicación publicada ni el
 * directorio son el panel.
 */
const published = () => !isPanelPath(location.pathname);
/**
 * Paleta que se muestra ahora: ninguna en las rutas publicadas, la de vista previa si hay o la
 * guardada.
 */
const shown = (): AppTheme => (published() ? { palette: null } : (preview ?? saved()));

$effect.root(() => {
  $effect(() => {
    const root = document.documentElement;
    const { palette, color } = shown();
    if (palette) root.dataset.palette = palette;
    else delete root.dataset.palette;
    // `custom` va con su color en línea; cualquier otra lo retira, porque en
    // línea gana a lo que diga su `data-palette`.
    if (palette === CUSTOM_PALETTE && color) root.style.setProperty("--palette-1", color);
    else root.style.removeProperty("--palette-1");
  });

  $effect(() => {
    if (!session.me) return;
    fallback = saved();
    localStorage.setItem(KEY, JSON.stringify(fallback));
  });
});

export const panelPalette = {
  /** La guardada en la cuenta. */
  get value(): AppTheme {
    return saved();
  },
  /** Pinta el panel con otra sin guardarla; `null` vuelve a la guardada. */
  preview(theme: AppTheme | null) {
    preview = theme ? clean(theme) : null;
  },
  /**
   * La guarda en la cuenta. El SDK actualiza la sesión con el registro que
   * devuelve, y de ahí la lee `session.me`: no hace falta apuntarla aparte.
   */
  async save(theme: AppTheme): Promise<void> {
    const me = session.me;
    if (!me) return;
    await pb.collection("builders").update(me.id, { palette: clean(theme) });
    preview = null;
  },
};
